"use client";

import clsx from "clsx";
import { Logo } from "@/components/logo";
import * as Viewport from "@/components/viewport";
import { useTouchInteraction } from "./hooks/useTouchInteraction";
import styles from "./logos-component.module.css";

type Section = "topshop" | "topman";

type LogosComponentProps = {
  clicked: Section | null;
  hovered: Section | null;
  handleClick: (target: Section) => void;
  setHovered: (section: Section | null) => void;
  isNavigating: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
};

export default function LogosComponent({
  clicked,
  hovered,
  handleClick,
  setHovered,
  isNavigating,
  containerRef,
}: LogosComponentProps) {
  const { handleTouchStart, handleTouchEnd, handleClickWithTouch } =
    useTouchInteraction(
      setHovered,
      handleClick,
      clicked,
      isNavigating,
      containerRef,
    );

  const getSectionClass = (section: Section) => {
    return clsx(styles.section, styles[section], {
      [styles.clicked as string]: clicked === section,
      [styles.notClicked as string]: clicked && clicked !== section,
      [styles.hovered as string]: !clicked && hovered === section,
      [styles.shrunk as string]: !clicked && hovered && hovered !== section,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent, section: Section) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick(section);
    }
  };

  return (["topshop", "topman"] as Section[]).map((section) => (
    <div
      key={section}
      className={getSectionClass(section)}
      role="button"
      tabIndex={0}
      onMouseEnter={() => !clicked && !isNavigating && setHovered(section)}
      onMouseLeave={() => !clicked && !isNavigating && setHovered(null)}
      onClick={() => handleClickWithTouch(section)}
      onTouchStart={(e) => handleTouchStart(e, section)}
      onTouchEnd={handleTouchEnd}
      onKeyDown={(e) => handleKeyDown(e, section)}
    >
      <div className={styles.logoWrapper}>
        <Viewport.Large>
          <Logo variant={section} height={72} />
        </Viewport.Large>
        <Viewport.Small>
          <Logo variant={section} height={36} />
        </Viewport.Small>
      </div>
    </div>
  ));
}
